/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Ladda from 'ladda';

import { validateForm } from '@/util/ParsleyUtils';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showModal from '@/util/showModal';
import HelpModalView from '@/views/HelpModalView';

import UserInfoView from '../../../administration/views/UserInfoView/UserInfoView';
import UserSearchModalView from '../../../administration/views/UserSearchModalView/UserSearchModalView';
import CreateOrder from '../../services/CreateOrder';
import UpdateUserActingAsCommand from '../../../external-dashboard/services/UpdateUserActingAsCommand';
import OrderHeaderView from '../../views/OrderHeaderView/OrderHeaderView';
import './CreateOrderPage.css';

import tmplCreateOrderPage from './CreateOrderPage.hbs';

const CreateOrderPage = View.extend({
  template: tmplCreateOrderPage,

  className: 'create-order-page container-fluid',

  ui: {
    orderHeader: '.order-header',
    orderFormTypeID: '#orderFormTypeID',
    selectOrderTypeForm: '.select-order-type-form',
    btnCreateOrder: '.btn-create-order',
    loadingMessage: '#loadingMessage',
    btnSelectUser: '.btn-select-user',
    userInfoView: '.user-info-view',
    btnSwitchUser: '.btn-switch-user',
    reqProtectionFromEmanations: '[name="reqProtectionFromEmanations"]',
    emanationsNoMessage: '.emanationsNoMessage',
    emanationsYesMessage: '.emanationsYesMessage',
    btnHelp: '#btnHelp'
  },

  regions: {
    orderHeader: '@ui.orderHeader',
    userInfoView: '@ui.userInfoView'
  },

  events: {
    'change @ui.orderFormTypeID': 'onChangeOrderFormType',
    'click @ui.btnCreateOrder': 'onClickCreateOrder',
    'click @ui.btnSelectUser': 'onClickSelectUser',
    'click @ui.btnSwitchUser': 'onClickSelectUser',
    'change @ui.reqProtectionFromEmanations': 'onChangeProtectionFromEmanations',
    'click @ui.btnHelp': 'onClickHelpBtn'
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  initialize() {
    const orderFormTypes = this.model.get('orderFormTypes');
    const displayOrderFormTypes = _.filter(orderFormTypes, (type) => type.isDisplayed === '1');
    this.model.set('displayOrderFormTypes', displayOrderFormTypes);
  },

  onRender() {
    this.ui.loadingMessage.hide();
    this.ui.btnSwitchUser.hide();
    this.ui.emanationsYesMessage.hide();
    this.ui.emanationsNoMessage.hide();

    this.showChildView(
      'orderHeader',
      new OrderHeaderView({
        model: new Backbone.Model({
          commandName: this.model.get('commandName'),
          commandId: this.model.get('commandId'),
          internalUserCreate: this.model.get('internalUser')
        })
      })
    );
  },

  async onClickSelectUser() {
    try {
      await showModal({
        title: 'Select User',
        view: new UserSearchModalView({
          model: new Backbone.Model({
            searchType: 'ExternalUserSearchByCommandId',
            commandId: this.model.get('commandId')
          })
        }),
        dialogClasses: 'modal-lg'
      }).then(async (user) => {
        user.fullName = `${user.firstName} ${user.lastName}`;
        user.userId = user.otdsUserId;
        this.showAddSuccess();
        this.onUserSelected(user);
        this.ui.btnSwitchUser.show();
        this.ui.btnSelectUser.hide();
      });
    } catch (err) {
      this.showAddError();
    }
  },

  async onUserSelected(user) {
    try {
      this.model.set('selectedUser', user);
      this.showUserInfoView(user);
    } catch (err) {
      this.showUserInfoError();
      return;
    }
  },

  showUserInfoView(user) {
    const model = new Backbone.Model(user);
    model.set('createPage', true);
    const userInfoView = new UserInfoView({ model });
    this.showChildView('userInfoView', userInfoView);
  },

  showUserInfoError() {
    showErrorMessage({
      errorCode: 'userInfoError',
      errorTitle: 'User Information Error',
      errorMessage: 'Unable to set selected user. Please try again or contact and administrator.'
    });
  },

  showAddSuccess() {
    showSuccessMessage({
      code: 'addSuccess',
      title: 'Customer Added',
      message: 'Customer selected.',
      hideAfter: 5
    });
  },

  showAddError() {
    showErrorMessage({
      errorCode: 'addError',
      errorTitle: 'Customer Add Error',
      errorMessage:
        'An error occurred while selecting customer. Try again or contact an administrator if your issue persists.'
    });
  },

  onChangeProtectionFromEmanations() {
    const reqProtectionFromEmanations = $(`input[name="reqProtectionFromEmanations"]:checked`).val()

    if(reqProtectionFromEmanations === '1') {
      this.ui.emanationsYesMessage.show();
      this.ui.emanationsNoMessage.hide();
    } else if(reqProtectionFromEmanations === '0') {
      this.ui.emanationsYesMessage.hide();
      this.ui.emanationsNoMessage.show();
    } else {
      this.ui.emanationsYesMessage.hide();
      this.ui.emanationsNoMessage.hide();
    }
  },

  async onClickHelpBtn() {
    showModal({
      title: 'Need Help?',
      view: new HelpModalView({})
    });
  },

  showGetUserGuideError() {
    showErrorMessage({
      errorCode: 'documentError',
      errorTitle: 'Document Error',
      errorMessage:
        'An error occurred while getting the User Guide. Try again or contact an administrator if your issue persists.'
    });
  },

  onChangeOrderFormType() {
    const orderFormTypes = this.model.get('orderFormTypes');
    const orderFormType = _.find(orderFormTypes, (x) => x.id === this.ui.orderFormTypeID.val().toString())?.formName;
    Radio.channel('order').trigger('update:header', 'orderType', orderFormType);
  },

  async onClickCreateOrder(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnCreateOrder[0]);
    spinner.start();

    try {
      await validateForm(this.ui.selectOrderTypeForm);
      const formData = Syphon.serialize(this);
      this.toggleForm(false);

      if (this.model.get('internalUser')) {
        const user = this.model.get('selectedUser');
        const OTDSUserID = user.otdsUserId;
        setTimeout(() => {
          this.ui.loadingMessage.show();
        }, 3000);
        const commandId = this.model.get('commandId');
        const orderId = await CreateOrder(formData.orderFormTypeID, OTDSUserID, commandId);
        Radio.channel('navigate').request('show:order', orderId, true);
      } else {
        setTimeout(() => {
          this.ui.loadingMessage.show();
        }, 3000);
        const [, orderId] = await Promise.all([
          UpdateUserActingAsCommand(this.model.get('commandId')), // Ensure the user is acting as the same command as shown in the header
          CreateOrder(formData.orderFormTypeID)
        ]);

        Radio.channel('navigate').request('show:ext:order', orderId, true);
      }
    } catch (err) {
      this.toggleForm(true);
      this.ui.loadingMessage.hide();
      spinner.stop();

      if (typeof err.message === 'string') {
        if (err.message.includes('otdsUserId')) {
          showErrorMessage({
            errorCode: 'missingExternalUser',
            errorTitle: 'Missing External User',
            errorMessage: 'Please select an external user to create order.'
          });
        }
      } else if (err) {
        showErrorMessage({
          errorCode: 'orderCreationError',
          errorTitle: 'Order Creation Error',
          errorMessage:
            'An error occurred while creating this order. If it has been 30+ seconds since attempting to create order, there may be ongoing network issues. Please contact an administrator if your issue persists.'
        });
      }
    }
  }
});

export default CreateOrderPage;